// Product and marketing analytics.
//
// One call (`track`) sends an event to PostHog (if loaded), the GTM
// dataLayer (GA4) and, for selected events, the Meta Pixel. Every
// destination is optional and failures never affect the page.

const ATTRIBUTION_KEY = "teache_attribution";
const ATTRIBUTION_PARAMS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "gclid",
    "fbclid",
];

const safeStorage = {
    get(key) {
        try {
            return JSON.parse(window.localStorage.getItem(key));
        } catch {
            return null;
        }
    },
    set(key, value) {
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // storage unavailable (private mode, blocked): ignore
        }
    },
};

/** Send an event to all configured analytics destinations. */
export const track = (event, props = {}) => {
    try {
        window.posthog?.capture?.(event, props);
    } catch {
        // ignore
    }
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({event, ...props});
    } catch {
        // ignore
    }
};

/**
 * Send a Meta Pixel event. Standard events (Lead, Contact, ...) use "track";
 * anything else uses "trackCustom". `eventId` lets Meta de-duplicate the
 * browser event against the same server-side Conversions API event.
 */
export const metaTrack = (name, params = {}, eventId) => {
    try {
        if (typeof window.fbq !== "function") return;
        const standard = ["Lead", "Contact", "CompleteRegistration", "ViewContent", "InitiateCheckout", "AddPaymentInfo", "Purchase"];
        const options = eventId ? {eventID: eventId} : undefined;
        window.fbq(standard.includes(name) ? "track" : "trackCustom", name, params, options);
    } catch {
        // ignore
    }
};

/** PostHog's anonymous id for this browser, used to join pre-signup activity. */
export const getAnonId = () => {
    try {
        return window.posthog?.get_distinct_id?.() || "";
    } catch {
        return "";
    }
};

/**
 * Store the ad/campaign parameters of the landing URL: first touch is kept
 * forever, last touch is replaced by any newer campaign visit.
 */
export const captureAttribution = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        const touch = {};
        ATTRIBUTION_PARAMS.forEach((key) => {
            const value = params.get(key);
            if (value) touch[key] = value.slice(0, 200);
        });
        const stored = safeStorage.get(ATTRIBUTION_KEY) || {};
        const now = new Date().toISOString();
        const landing = {
            landing_page: window.location.pathname.slice(0, 200),
            referrer: (document.referrer || "").slice(0, 200),
            at: now,
        };

        if (!stored.first_touch) {
            stored.first_touch = {...touch, ...landing};
        }
        if (Object.keys(touch).length > 0) {
            stored.last_touch = {...touch, ...landing};
            if (touch.fbclid) {
                stored.fbc = `fb.1.${Date.now()}.${touch.fbclid}`;
            }
        }
        safeStorage.set(ATTRIBUTION_KEY, stored);
    } catch {
        // ignore
    }
};

/** Stored first/last touch attribution. */
export const getAttribution = () => {
    const stored = safeStorage.get(ATTRIBUTION_KEY) || {};
    return {first_touch: stored.first_touch || null, last_touch: stored.last_touch || null};
};

const readCookie = (name) => {
    try {
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : "";
    } catch {
        return "";
    }
};

/** Meta browser identifiers for Conversions API matching. */
export const getMetaIds = () => ({
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc") || (safeStorage.get(ATTRIBUTION_KEY) || {}).fbc || "",
});
